<template>
    <div
        class="pricing-card shadow-3 border-round-xl surface-card p-5 border-1 border-200 flex flex-column align-items-center h-full transition-all transition-duration-300 hover:shadow-6">

        <h2 class="text-900 text-xl font-bold mb-1">{{ packageName }}</h2>
        <span class="text-500 text-sm mb-4">Code: {{ packageCode }}</span>

        <div class="flex align-items-baseline gap-1 mb-4">
            <span class="text-600 text-lg font-medium">LKR</span>
            <span class="text-4xl font-bold text-900">{{ Number(form.amount).toFixed(2) }}</span>
        </div>

        <Divider class="w-full my-3" />

        <Button label="Buy Now" icon="pi pi-shopping-cart" class="w-full p-button-raised border-round-lg font-bold"
            :loading="loading" @click="openBillingDialog" />

        <Dialog v-model:visible="showBillingDialog" modal header="Billing Details" :style="{ width: '650px' }"
            class="p-fluid" :closable="!loading">
            <div class="p-1">
                <p class="mb-4 text-600 text-sm">Confirm your details to proceed with PayHere.</p>

                <div class="grid formgrid">
                    <div class="col-12 md:col-6 field mb-3">
                        <label class="font-bold text-sm block mb-2">First Name</label>
                        <InputText v-model="form.first_name" placeholder="First Name"
                            :class="{ 'p-invalid': submitted && !form.first_name }" class="w-full" />
                    </div>

                    <div class="col-12 md:col-6 field mb-3">
                        <label class="font-bold text-sm block mb-2">Last Name</label>
                        <InputText v-model="form.last_name" placeholder="Last Name"
                            :class="{ 'p-invalid': submitted && !form.last_name }" class="w-full" />
                    </div>

                    <div class="col-12 md:col-6 field mb-3">
                        <label class="font-bold text-sm block mb-2">Email Address</label>
                        <InputText v-model="form.email" disabled class="bg-gray-100 w-full" />
                    </div>

                    <div class="col-12 md:col-6 field mb-3">
                        <label class="font-bold text-sm block mb-2">Phone Number</label>
                        <InputText v-model="form.mobileNumber" placeholder="Phone Number"
                            :class="{ 'p-invalid': (submitted && !form.mobileNumber) || phoneError }" class="w-full" />
                        <small v-if="phoneError" class="p-error text-red-500">{{ phoneError }}</small>
                    </div>

                    <div class="col-12 md:col-6 field mb-3">
                        <label class="font-bold text-sm block mb-2">Street Address</label>
                        <InputText v-model="form.address" placeholder="Street Address"
                            :class="{ 'p-invalid': submitted && !form.address }" class="w-full" />
                    </div>

                    <div class="col-12 md:col-6 field mb-3">
                        <label class="font-bold text-sm block mb-2">City</label>
                        <InputText v-model="form.city" placeholder="City"
                            :class="{ 'p-invalid': submitted && !form.city }" class="w-full" />
                    </div>

                    <div class="col-12 md:col-6 field mb-3">
                        <label class="font-bold text-sm block mb-2">Country</label>
                        <InputText v-model="form.country" disabled class="bg-gray-100 w-full" />
                    </div>
                </div>
            </div>

            <template #footer>
                <Button label="Cancel" class="p-button-text p-button-secondary" @click="showBillingDialog = false"
                    :disabled="loading" />
                <Button label="Pay Now" icon="pi pi-credit-card" @click="validateAndPay" :loading="loading"
                    severity="success" />
            </template>
        </Dialog>

    </div>
</template>

<script setup>
import { ref, reactive, defineProps, watch, toRaw, defineEmits } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dialog from 'primevue/dialog';
import { useRouter } from 'vue-router';
import payments from '@/api/payments';
import { notifyError, notifyContrast } from '@/utils/notification';
import { updateProfile } from '@/api/tutor';

const props = defineProps({
    packageName: { type: String, required: true },
    packageCode: { type: String, required: true },
    amount: { type: Number, required: true },
    profile: { type: Object, required: false },

    initialFields: { type: Object, required: false, default: () => ({}) }
});

const emit = defineEmits(['profile-updated']);

const loading = ref(false);
const router = useRouter();
const showBillingDialog = ref(false);
const submitted = ref(false);
const phoneError = ref('');

// Form Data
const form = reactive({
    items: props.initialFields.items || props.packageCode,
    order_id: props.initialFields.order_id || `ItemNo${Math.floor(Math.random() * 100000)}`,
    currency: props.initialFields.currency || 'LKR',
    amount: props.initialFields.amount || props.amount || 0,
    first_name: props.initialFields.first_name || props.profile?.firstName || '',
    last_name: props.initialFields.last_name || props.profile?.lastName || '',
    email: props.initialFields.email || props.profile?.email || '',
    mobileNumber: props.initialFields.mobileNumber || props.profile?.mobileNumber || '',
    address: props.initialFields.address || props.profile?.address || '',
    city: props.initialFields.city || props.profile?.city || '',
    country: props.initialFields.country || 'Sri Lanka'
});

watch(() => props.profile, (newProfile) => {
    if (newProfile) {
        form.first_name = newProfile.firstName || form.first_name;
        form.last_name = newProfile.lastName || form.last_name;
        form.email = newProfile.email || form.email;
        form.mobileNumber = newProfile.mobileNumber || form.mobileNumber;
        form.address = newProfile.address || form.address;
        form.city = newProfile.city || form.city;
    }
}, { immediate: true });

const openBillingDialog = () => {
    submitted.value = false;
    phoneError.value = '';
    showBillingDialog.value = true;
};

const validateAndPay = async () => {
    submitted.value = true;
    phoneError.value = '';
    const requiredFields = ['first_name', 'last_name', 'email', 'mobileNumber', 'address', 'city', 'country'];
    const isValid = requiredFields.every(field => form[field] && form[field].toString().trim() !== '');

    if (!isValid) {
        notifyError('Validation Error', 'Please fill in all required fields.');
        return;
    }

    const phoneRegex = /^(?:\+94|0)?7\d{8}$/;
    if (!phoneRegex.test(form.mobileNumber)) {
        phoneError.value = 'Please enter a valid mobile number.';
        return;
    }

    // Update tutor profile with missing data
    const updatePayload = {};
    if (!props.profile?.firstName && form.first_name) updatePayload.firstName = form.first_name;
    if (!props.profile?.lastName && form.last_name) updatePayload.lastName = form.last_name;
    if (!props.profile?.mobileNumber && form.mobileNumber) updatePayload.mobileNumber = form.mobileNumber;
    if (!props.profile?.address && form.address) updatePayload.address = form.address;
    if (!props.profile?.city && form.city) updatePayload.city = form.city;

    if (Object.keys(updatePayload).length > 0) {
        try {
            await updateProfile(updatePayload);
            if (props.profile) {
                Object.assign(props.profile, updatePayload);
            }
            emit('profile-updated');
        } catch (error) {
            console.error('Failed to update profile with missing fields:', error);
        }
    }

    await startPayment();
};

/**
 * Initiate the payment process
 */
const startPayment = async () => {
    loading.value = true;
    try {
        notifyContrast('Preparing Payment', 'Please wait...');

        const mapped = {
            firstName: form.first_name,
            lastName: form.last_name,
            orderId: form.order_id,
            items: form.items,
            currency: form.currency,
            amount: Number(form.amount).toFixed(2),
            email: form.email,
            phone: form.mobileNumber,
            address: form.address,
            city: form.city,
            country: form.country,
            first_name: form.first_name,
            last_name: form.last_name,
            order_id: form.order_id,
        };

        const params = {
            packageCode: props.packageCode,
            ...toRaw(form),
            ...mapped
        };

        const res = await payments.initiate(params);
        const payload = res?.data?.data;

        if (!payload) {
            throw new Error('Invalid payment response');
        }

        if (payload.redirectUrl) {
            window.location.href = payload.redirectUrl;
            return;
        }

        payments.processPayHereRedirect(payload, toRaw(form));

    } catch (err) {
        notifyError('Payment Error', err?.response?.data?.message || err.message || 'Failed to start payment');
        router.push('/payments/failed');
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped>
.pricing-card {
    transition: transform 0.2s ease-in-out;
}

.pricing-card:hover {
    transform: translateY(-5px);
}
</style>
